import { APIError } from "../errors";
import * as drf from "./drf";
import { type ApiSettings, type CasingIgnorePaths } from "./drf";
import config from "config";

const defaultSettings: ApiSettings = {
  domain: config.get("apiDomain") as string,
  hasMultipleQueryParamValues: false,
  headers: {},
};

type SettingsOverride = Partial<ApiSettings>;

function mergeSettings(
  base: ApiSettings,
  override?: SettingsOverride,
): ApiSettings {
  if (!override) return base;
  return {
    ...base,
    ...override,
    headers: { ...base.headers, ...override.headers },
  };
}

export type ApiType = {
  configure: (override: SettingsOverride) => void;
  delete: <T = unknown>(
    resource: string,
    data?: unknown,
    override?: SettingsOverride,
    ignorePaths?: CasingIgnorePaths,
  ) => Promise<T>;
  get: <T = unknown>(
    resource: string,
    override?: SettingsOverride,
    params?: Record<string, unknown>,
    ignorePaths?: CasingIgnorePaths,
  ) => Promise<T>;
  patch: <T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ) => Promise<T>;
  post: <T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ) => Promise<T>;
  put: <T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ) => Promise<T>;
  reset: () => void;
  setDomain: (domain: string) => void;
  url: (
    resource: string,
    override?: SettingsOverride,
    params?: Record<string, unknown>,
  ) => string;
};

class ApiClient implements ApiType {
  private settings: ApiSettings = defaultSettings;

  reset(): void {
    this.settings = defaultSettings;
  }

  configure(override: SettingsOverride): void {
    this.settings = mergeSettings(this.settings, override);
  }

  setDomain(domain: string): void {
    const headerName = config.get("sitesDomainRequestHeader") as string;
    this.configure({ headers: { [headerName]: domain } });
  }

  get<T = unknown>(
    resource: string,
    override?: SettingsOverride,
    params?: Record<string, unknown>,
    ignorePaths?: CasingIgnorePaths,
  ): Promise<T> {
    return drf.get<T>(
      resource,
      params,
      mergeSettings(this.settings, override),
      ignorePaths,
    );
  }

  post<T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ): Promise<T> {
    return drf.post<T>(
      resource,
      data,
      mergeSettings(this.settings, override),
      ignorePaths,
    );
  }

  patch<T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ): Promise<T> {
    return drf.patch<T>(
      resource,
      data,
      mergeSettings(this.settings, override),
      ignorePaths,
    );
  }

  put<T = unknown>(
    resource: string,
    override?: SettingsOverride,
    data?: unknown,
    ignorePaths?: CasingIgnorePaths,
  ): Promise<T> {
    return drf.put<T>(
      resource,
      data,
      mergeSettings(this.settings, override),
      ignorePaths,
    );
  }

  delete<T = unknown>(
    resource: string,
    data?: unknown,
    override?: SettingsOverride,
    ignorePaths?: CasingIgnorePaths,
  ): Promise<T> {
    return drf.del<T>(
      resource,
      data,
      mergeSettings(this.settings, override),
      ignorePaths,
    );
  }

  url(
    resource: string,
    override?: SettingsOverride,
    params?: Record<string, unknown>,
  ): string {
    return drf.makeUrl(
      resource,
      params,
      mergeSettings(this.settings, override),
    );
  }
}

export default new ApiClient() as ApiType;
export { ApiClient, APIError };
